import {
  UserView as ApiUser,
  PermissionView,
  RoleUserView,
  RoleView,
  UserCompanyView,
  UserLoginView,
} from '../generated/generated';

import * as z from 'zod';
import { Column } from './column.model';

type Permission = PermissionView;
type UserRole = RoleUserView;
type Role = RoleView;
type UserCompany = UserCompanyView;
type UserLogin = Omit<UserLoginView, 'company'> & {
  companyName: string | null;
};

const bitToBoolean = z.codec(
  z.number(), // input schema
  z.boolean(), // output schema
  {
    decode: (nVal) => !!nVal,
    encode: (bVal) => +bVal,
  },
);

const UserSchema = z.object({
  id: z.string(),
  email: z.email(),
  salutation: z.string().nullable(),
  active: bitToBoolean,
  name: z.string(),
  title: z.string().nullable(),
  createdAt: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  companyName: z.string().nullable(),
  companyId: z.number().nullable(),
  roles: z.string().array(),
});

type User = z.infer<typeof UserSchema>;

const columns: Column<keyof User>[] = [
  {
    header: 'column.name',
    style: 'min-width: 15rem',
    field: 'name',
    filterField: 'name',
    sortField: 'name',
    type: 'text',
  },
  {
    header: 'column.email',
    style: 'min-width: 15rem',
    field: 'email',
    filterField: 'email',
    sortField: 'email',
    type: 'text',
  },
  {
    header: 'column.roles',
    style: 'min-width: 15rem',
    field: 'roles',
    filterField: 'roles',
    type: 'tags',
    placeholder: 'placeholder.selectRole',
    textOptions: ['user', 'admin', 'user-123'],
  },
  {
    header: 'column.active',
    field: 'active',
    filterField: 'active',
    sortField: 'active',
    type: 'boolean',
  },
  {
    header: 'column.company',
    style: 'min-width: 15rem',
    field: 'companyName',
    filterField: 'company.name',
    type: 'text',
  },
  {
    header: 'column.salutation',
    style: 'min-width: 15rem',
    field: 'salutation',
    filterField: 'salutation',
    sortField: 'salutation',
    type: 'text',
  },
  {
    header: 'column.title',
    style: 'min-width: 15rem',
    field: 'title',
    filterField: 'title',
    sortField: 'title',
    type: 'text',
  },
  {
    header: 'column.firstName',
    style: 'min-width: 15rem',
    field: 'firstName',
    filterField: 'firstName',
    sortField: 'firstName',
    type: 'text',
  },
  {
    header: 'column.lastName',
    style: 'min-width: 15rem',
    field: 'lastName',
    filterField: 'lastName',
    sortField: 'lastName',
    type: 'text',
  },
];
const globalFilterFields = [
  'company.name',
  'email',
  'firstName',
  'lastName',
  'name',
  'salutation',
  'title',
];
const User = UserSchema.meta({
  columns,
  globalFilterFields,
});

export { ApiUser, Permission, Role, User, UserCompany, UserLogin, UserRole };
